import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { formatFileSize } from "../utils/fileHelpers";

const OutputTab = ({
  publicAgents,
  selectedOutputAgentId,
  setSelectedOutputAgentId,
  outputResponse,
  outputDownloadedFiles,
  isExecutingOutput,
  isEditingOutputPrompt,
  outputPromptText,
  setOutputPromptText,
  loadingAgents = false,
  aiRequestDebug,
}) => {
  const debugText = aiRequestDebug?.aiRequestInput
    ? JSON.stringify(aiRequestDebug.aiRequestInput, null, 2)
    : "No AI request input yet";

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "auto",
        backgroundColor: "#faf5ff",
      }}
    >
      {/* Agent Selection */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          p: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#374151",
            fontWeight: 600,
            fontSize: "11px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1.5,
            display: "block",
          }}
        >
          Select AI Agent
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ fontSize: "13px" }}>Public AI Agent</InputLabel>
          <Select
            value={selectedOutputAgentId}
            onChange={(e) => setSelectedOutputAgentId(e.target.value)}
            label="Public AI Agent"
            MenuProps={{
              sx: {
                zIndex: 9999,
              },
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                },
              },
            }}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "6px",
              fontSize: "13px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e2e8f0",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e0",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#8b5cf6",
                borderWidth: "1px",
              },
            }}
          >
            <MenuItem value="" disabled sx={{ fontSize: "13px" }}>
              <em>Select an agent</em>
            </MenuItem>
            {publicAgents.map((agent) => (
              <MenuItem
                key={agent.id}
                value={agent.id}
                sx={{ fontSize: "13px" }}
              >
                {agent.name}
                {agent.description && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      color: "#94a3b8",
                      fontSize: "11px",
                    }}
                  >
                    — {agent.description.substring(0, 40)}
                    {agent.description.length > 40 ? "..." : ""}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingAgents && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <CircularProgress size={14} sx={{ color: "#8b5cf6" }} />
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Loading agents...
            </Typography>
          </Box>
        )}
        {!loadingAgents && publicAgents.length === 0 && (
          <Typography
            variant="caption"
            sx={{ color: "#f59e0b", mt: 1, display: "block" }}
          >
            No public AI agents available. Create agents with
            &quot;isPublic&quot; enabled in Spark page.
          </Typography>
        )}
      </Box>

      {/* Custom Prompt (shown when editing) */}
      {isEditingOutputPrompt && (
        <Box
          sx={{
            backgroundColor: "#fffbeb",
            borderRadius: "8px",
            border: "1px solid #fcd34d",
            p: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#92400e",
              fontWeight: 600,
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 1,
              display: "block",
            }}
          >
            Custom Prompt (Optional)
          </Typography>
          <TextField
            value={outputPromptText}
            onChange={(e) => setOutputPromptText(e.target.value)}
            placeholder="Add custom instructions for this AI execution..."
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                fontSize: "13px",
                "& fieldset": { borderColor: "#fcd34d" },
                "&:hover fieldset": { borderColor: "#f59e0b" },
                "&.Mui-focused fieldset": {
                  borderColor: "#f59e0b",
                  borderWidth: "1px",
                },
              },
            }}
          />
        </Box>
      )}

      {/* Response Section */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#374151",
                fontWeight: 600,
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Response
            </Typography>
            {outputResponse && (
              <Chip
                label="Complete"
                size="small"
                sx={{
                  backgroundColor: "#22c55e",
                  color: "#ffffff",
                  fontSize: "10px",
                  height: 20,
                }}
              />
            )}
            {isExecutingOutput && (
              <Chip
                label="Processing..."
                size="small"
                sx={{
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  fontSize: "10px",
                  height: 20,
                }}
              />
            )}
          </Box>

          {/* <Tooltip
            title={
              <Box
                component="pre"
                sx={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: "11px",
                  maxWidth: 520,
                  maxHeight: 420,
                  overflow: "auto",
                }}
              >
                {debugText}
              </Box>
            }
            placement="left"
            arrow
            PopperProps={{
              sx: {
                zIndex: 20000,
              },
            }}
          >
            <IconButton size="small" sx={{ color: "#64748b" }}>
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip> */}
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
          }}
        >
          {isExecutingOutput ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#8b5cf6" }} />
              <Typography variant="body2" sx={{ color: "#64748b", mt: 2 }}>
                Executing AI request...
              </Typography>
            </Box>
          ) : outputResponse ? (
            <Box
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "12px",
                color: "#334155",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                margin: 0,
                backgroundColor: "#ffffff",
                p: 2,
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
              }}
            >
              {typeof outputResponse === "string"
                ? outputResponse
                : JSON.stringify(outputResponse, null, 2)}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                color: "#94a3b8",
              }}
            >
              <DescriptionIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="body2">
                Select an agent and click the spark button to execute
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Downloaded Files Section */}
      {outputDownloadedFiles.length > 0 && (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#374151",
                  fontWeight: 600,
                  fontSize: "11px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Generated Files
              </Typography>
              <Chip
                label={outputDownloadedFiles.length}
                size="small"
                sx={{
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                  fontSize: "10px",
                  height: 20,
                  minWidth: 24,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ p: 1 }}>
            {outputDownloadedFiles.map((downloadedFile) => (
              <Box
                key={downloadedFile.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  px: 1.5,
                  borderRadius: "6px",
                  "&:hover": {
                    backgroundColor: "#f0f9ff",
                  },
                }}
              >
                <FileIcon sx={{ fontSize: 20, color: "#64748b" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "13px",
                      color: "#334155",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {downloadedFile.description ||
                      downloadedFile.name ||
                      "AI Generated File"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      color: "#94a3b8",
                    }}
                  >
                    {downloadedFile.mimeType || downloadedFile.type}{" "}
                    {downloadedFile.size &&
                      `• ${formatFileSize(downloadedFile.size)}`}
                  </Typography>
                </Box>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={() => window.open(downloadedFile.url, "_blank")}
                    sx={{
                      color: "#64748b",
                      "&:hover": {
                        backgroundColor: "#dbeafe",
                        color: "#3b82f6",
                      },
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OutputTab;
